<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div
    class="calendar-input-container"
    ref="calendarContainer"
    :class="{ inline: inline }"
  >
    <div v-if="!inline && !showTimeOnly" class="input-wrapper">
      <input
        :value="formattedDate"
        :placeholder="placeholder"
        class="calendar-input"
        :class="{ 'p-invalid': validation?.$error || modelstate }"
        :disabled="disabled"
        :id="id"
        :aria-describedby="`${id}-help`"
        @click="toggleCalendar"
        @keydown="handleInputKeydown"
        readonly
        ref="input"
      />
      <span
        v-if="showIcon"
        class="calendar-icon"
        @click="toggleCalendar"
        tabindex="0"
        @keydown="handleIconKeydown"
      >
        <i class="cal-icon"></i>
      </span>
    </div>
    <div v-if="showTimeOnly" class="input-wrapper">
      <input
        :value="formattedTime"
        :placeholder="'Select time'"
        class="calendar-input"
        :class="{ 'p-invalid': validation?.$error || modelstate }"
        :disabled="disabled"
        :id="id"
        :aria-describedby="`${id}-help`"
        @click="toggleCalendar"
        @keydown="handleInputKeydown"
        readonly
        ref="input"
      />
      <span
        v-if="showIcon"
        class="calendar-icon"
        @click="toggleCalendar"
        tabindex="0"
        @keydown="handleIconKeydown"
      >
        <i class="cal-icon"></i>
      </span>
    </div>
    <span
      v-if="validation?.$error || modelstate"
      :id="`${id}-help`"
      class="p-error text-left mt-2"
    >
      {{ validation?.$errors[0]?.$message || modelstate }}
    </span>
    <div
      v-if="showCalendar || inline"
      class="calendar-container"
      :style="panelStyle"
      :class="[
        numberOfMonths == 1
          ? 'calendar-container-width1'
          : 'calendar-container-width2',
        { 'calendar-downside': CalendarDownside }
      ]"
      ref="calendarPanel"
    >
      <div class="calendar-month-container" v-if="!showTimeOnly">
        <div
          v-for="monthIndex in numberOfMonths"
          :key="monthIndex"
          class="calendar-month"
        >
          <div class="calendar-header">
            <button
              v-if="monthIndex === 1"
              @click="previousMonth"
              class="nav-button"
              aria-label="Previous month"
              ref="prevButton"
            >
              <i class="cal-lt"></i>
            </button>
            <span class="month-year"
              >{{ monthNames[currentMonths[monthIndex - 1].month] }}
              {{ currentMonths[monthIndex - 1].year }}</span
            >
            <button
              v-if="monthIndex === numberOfMonths"
              @click="nextMonth"
              class="nav-button"
              :disabled="isNextMonthDisabled(monthIndex - 1)"
              aria-label="Next month"
              ref="nextButton"
            >
              <i class="cal-gt"></i>
            </button>
          </div>
          <div class="calendar-grid" role="grid">
            <div class="day-header" v-for="day in daysOfWeek" :key="day">
              {{ day }}
            </div>
            <div
              v-for="(day, index) in calendarDays[monthIndex - 1]"
              :key="day.date"
              class="day"
              :class="{
                inactive: day.isInactive,
                'selected-start': isSelectedStart(day.date),
                'selected-end': isSelectedEnd(day.date),
                range: isInRange(day.date),
                today: isToday(day.date),
                disabled: isBeforeMinDate(day.date) || isAfterMaxDate(day.date),
              }"
              @click.stop="selectDate(day.date)"
              @keydown="handleDayKeydown($event, index, monthIndex - 1)"
              role="gridcell"
              :aria-label="formatDayAriaLabel(day)"
              :tabindex="
                isSelectedStart(day.date) || isSelectedEnd(day.date) ? 0 : -1
              "
            >
              {{ day.day }}
            </div>
          </div>
        </div>
      </div>
      <div v-if="showTime || showTimeOnly" class="time-picker">
        <div class="time-unit">
          <button
            @click="incrementHours"
            class="time-arrow up"
            aria-label="Increment hours"
            :disabled="isIncrementHoursDisabled"
          >
            <i class="up-cal"></i>
          </button>
          <span
            class="time-value"
            aria-label="Hours"
            tabindex="0"
            @keydown="handleTimeKeydown($event, 'hours')"
            ref="hourInput"
          >
            {{ pad(displayHours) }}
          </span>
          <button
            @click="decrementHours"
            class="time-arrow down"
            aria-label="Decrement hours"
            :disabled="isDecrementHoursDisabled"
          >
            <i class="down-cal"></i>
          </button>
        </div>
        <span class="time-separator">:</span>
        <div class="time-unit">
          <button
            @click="incrementMinutes"
            class="time-arrow up"
            aria-label="Increment minutes"
            :disabled="isIncrementMinutesDisabled"
          >
            <i class="up-cal"></i>
          </button>
          <span
            class="time-value"
            aria-label="Minutes"
            tabindex="0"
            @keydown="handleTimeKeydown($event, 'minutes')"
            ref="minuteInput"
          >
            {{ pad(minutes) }}
          </span>
          <button
            @click="decrementMinutes"
            class="time-arrow down"
            aria-label="Decrement minutes"
            :disabled="isDecrementMinutesDisabled"
          >
            <i class="down-cal"></i>
          </button>
        </div>
        <div v-if="hourFormat === '12'" class="time-unit">
          <button
            @click="togglePeriod"
            class="time-arrow up"
            aria-label="Toggle AM/PM"
            ref="periodButton"
          >
            <i class="up-cal"></i>
          </button>
          <span
            class="time-value"
            aria-label="Period"
            tabindex="0"
            @keydown="handleTimeKeydown($event, 'period')"
            ref="periodInput"
          >
            {{ period }}
          </span>
          <button
            @click="togglePeriod"
            class="time-arrow down"
            aria-label="Toggle AM/PM"
            ref="periodButtonDown"
          >
            <i class="down-cal"></i>
          </button>
        </div>
      </div>
      <div v-if="showButtonBar && !showTimeOnly" class="button-bar">
        <button
          v-if="showClear"
          @click="clearDate"
          class="clear-button"
          aria-label="Clear date"
          ref="clearButton"
        >
          Clear
        </button>
        <button
          @click="selectToday"
          class="today-button"
          aria-label="Select today"
          ref="todayButton"
        >
          Today
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    modelValue: {
      type: [Date, String, Array],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Select a date",
    },
    validation: {
      type: Object,
      required: false,
    },
    modelstate: {
      type: String,
      default: "",
    },
    id: {
      type: String,
      default: "calendar-input",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    lang: {
      type: String,
      default: "en",
    },
    showTime: {
      type: Boolean,
      default: false,
    },
    showTimeOnly: {
      type: Boolean,
      default: false,
    },
    hourFormat: {
      type: String,
      default: "24",
    },
    dateFormat: {
      type: String,
      default: "yy-mm-dd",
    },
    showIcon: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: Date,
      default: null,
    },
    maxDate: {
      type: Date,
      default: null,
    },
    selectionMode: {
      type: String,
      default: "single",
      validator: (value) => ["single", "range"].includes(value),
    },
    showClear: {
      type: Boolean,
      default: false,
    },
    showButtonBar: {
      type: Boolean,
      default: false,
    },
    panelStyle: {
      type: Object,
      default: () => ({}),
    },
    inline: {
      type: Boolean,
      default: false,
    },
    numberOfMonths: {
      type: Number,
      default: 1,
      validator: (value) => value >= 1,
    },
    CalendarDownside: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const currentDate = new Date();
    let initialRange = [null, null];
    let initialHours = 0;
    let initialMinutes = 0;
    let initialPeriod = "AM";

    if (
      this.showTimeOnly &&
      typeof this.modelValue === "string" &&
      this.modelValue
    ) {
      const [time, period] = this.modelValue.includes(" ")
        ? this.modelValue.split(" ")
        : [this.modelValue, this.hourFormat === "12" ? "AM" : null];
      const [hoursStr, minutesStr] = time.split(":");
      const hours = parseInt(hoursStr, 10);
      const minutes = parseInt(minutesStr, 10);

      initialHours = isNaN(hours) ? 0 : hours;
      initialMinutes = isNaN(minutes) ? 0 : minutes;
      initialPeriod =
        this.hourFormat === "12" && period && ["AM", "PM"].includes(period)
          ? period
          : "AM";

      if (this.hourFormat === "12") {
        initialHours =
          initialPeriod === "PM" && hours < 12
            ? hours + 12
            : initialPeriod === "AM" && hours === 12
            ? 0
            : hours;
      } else if (this.hourFormat === "24" && !isNaN(hours)) {
        initialHours = hours % 24;
      }
    } else if (
      this.selectionMode === "single" &&
      this.modelValue &&
      !this.showTimeOnly
    ) {
      const date = new Date(this.modelValue);
      initialRange = [date, null];
      initialHours = date.getHours();
      initialMinutes = date.getMinutes();
      initialPeriod = initialHours >= 12 ? "PM" : "AM";
    } else if (
      this.selectionMode === "range" &&
      Array.isArray(this.modelValue) &&
      !this.showTimeOnly
    ) {
      initialRange = [
        this.modelValue[0] ? new Date(this.modelValue[0]) : null,
        this.modelValue[1] ? new Date(this.modelValue[1]) : null,
      ];
      const date = initialRange[0] || initialRange[1];
      if (date) {
        initialHours = date.getHours();
        initialMinutes = date.getMinutes();
        initialPeriod = initialHours >= 12 ? "PM" : "AM";
      }
    }
    const initialMonth = initialRange[0]
      ? initialRange[0].getMonth()
      : currentDate.getMonth();
    const initialYear = initialRange[0]
      ? initialRange[0].getFullYear()
      : currentDate.getFullYear();
    const currentMonths = Array.from(
      { length: this.numberOfMonths },
      (_, i) => {
        let month = initialMonth + i;
        let year = initialYear;
        if (month > 11) {
          month = month % 12;
          year += Math.floor((initialMonth + i) / 12);
        }
        return { month, year };
      }
    );
    return {
      showCalendar: false,
      currentMonths,
      selectedRange: initialRange,
      hours: initialHours,
      minutes: initialMinutes,
      period: initialPeriod,
      focusedDayIndex: null,
      monthNames: this.getMonthNames(),
      daysOfWeek: this.getDaysOfWeek(),
    };
  },
  computed: {
    displayHours() {
      if (this.hourFormat === "12") {
        return this.hours === 0
          ? 12
          : this.hours > 12
          ? this.hours - 12
          : this.hours;
      }
      return this.hours;
    },
    formattedDate() {
      if (this.selectionMode === "single") {
        if (!this.selectedRange[0]) return "";
        const date = this.selectedRange[0];
        let format = this.dateFormat;
        if (this.showTime) {
          format += ` ${this.hourFormat === "24" ? "HH:MM" : "hh:MM A"}`;
        }
        return this.formatDate(date, format);
      } else {
        const start = this.selectedRange[0];
        const end = this.selectedRange[1];
        if (!start) return "";
        let format = this.dateFormat;
        if (this.showTime) {
          format += ` ${this.hourFormat === "24" ? "HH:MM" : "hh:MM A"}`;
        }
        const startFormatted = this.formatDate(start, format);
        const endFormatted = end ? this.formatDate(end, format) : "";
        return end ? `${startFormatted} - ${endFormatted}` : startFormatted;
      }
    },
    formattedTime() {
      if (!this.modelValue) return "";
      let hours = this.displayHours;
      const minutes = this.pad(this.minutes);
      if (this.hourFormat === "12") {
        return `${this.pad(hours)}:${minutes} ${this.period}`;
      }
      return `${this.pad(hours)}:${minutes}`;
    },
    calendarDays() {
      return this.currentMonths.map(({ month, year }) => {
        const days = [];
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        const startDay = firstDayOfMonth.getDay();
        const totalDays = lastDayOfMonth.getDate();
        const prevMonthDays = new Date(year, month, 0).getDate();
        for (let i = startDay; i > 0; i--) {
          days.push({
            day: prevMonthDays - i + 1,
            isInactive: true,
            date: new Date(year, month - 1, prevMonthDays - i + 1),
          });
        }
        for (let i = 1; i <= totalDays; i++) {
          days.push({
            day: i,
            isInactive: false,
            date: new Date(year, month, i),
          });
        }
        const remainingDays = 42 - days.length;
        for (let i = 1; i <= remainingDays; i++) {
          days.push({
            day: i,
            isInactive: true,
            date: new Date(year, month + 1, i),
          });
        }
        return days;
      });
    },
    isSelectedDateToday() {
      if (this.showTimeOnly) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = this.selectedRange[0]
        ? new Date(this.selectedRange[0])
        : null;
      if (!selectedDate) return false;
      selectedDate.setHours(0, 0, 0, 0);
      return selectedDate.getTime() === today.getTime();
    },
    isIncrementHoursDisabled() {
      if (!this.isSelectedDateToday) return false;
      if (this.hourFormat === "12") {
        return (
          (this.period === "PM" && this.hours >= 23) ||
          (this.period === "AM" && this.hours >= 11)
        );
      }
      return this.hours >= 23;
    },
    isDecrementHoursDisabled() {
      if (!this.isSelectedDateToday) return false;
      const currentDate = new Date();
      if (this.hourFormat === "12") {
        return (
          (this.period === "AM" && this.hours <= 0) ||
          (this.period === "PM" &&
            this.hours <= 12 &&
            this.hours <= currentDate.getHours())
        );
      }
      return this.hours <= currentDate.getHours();
    },
    isIncrementMinutesDisabled() {
      if (!this.isSelectedDateToday) return false;
      const currentDate = new Date();
      return this.hours === currentDate.getHours() && this.minutes >= 59;
    },
    isDecrementMinutesDisabled() {
      if (!this.isSelectedDateToday) return false;
      const currentDate = new Date();
      return (
        this.hours === currentDate.getHours() &&
        this.minutes <= currentDate.getMinutes()
      );
    },
  },
  methods: {
    isNextMonthDisabled(monthIndex) {
      if (!this.maxDate) return false;
      const nextMonth = new Date(
        this.currentMonths[monthIndex].year,
        this.currentMonths[monthIndex].month + 1,
        1
      );
      const maxDateMonth = new Date(
        this.maxDate.getFullYear(),
        this.maxDate.getMonth(),
        1
      );
      return nextMonth > maxDateMonth;
    },
    pad(num) {
      return String(num).padStart(2, "0");
    },
    getMonthNames() {
      const formatter = new Intl.DateTimeFormat(this.lang, { month: "long" });
      return Array.from({ length: 12 }, (_, i) =>
        formatter.format(new Date(2023, i, 1))
      );
    },
    getDaysOfWeek() {
      const formatter = new Intl.DateTimeFormat(this.lang, {
        weekday: "short",
      });
      return Array.from({ length: 7 }, (_, i) =>
        formatter.format(new Date(2023, 1, i + 5))
      );
    },
    formatDate(date, formatStr) {
      const pad = (num) => String(num).padStart(2, "0");
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      let hours =
        this.hourFormat === "12" ? this.displayHours : date.getHours();
      const minutes = pad(date.getMinutes());
      const period = this.hourFormat === "12" ? this.period : "";
      hours = pad(hours);
      return formatStr
        .replace("yyyy", year)
        .replace("yy", String(year).slice(-2))
        .replace("mm", month)
        .replace("dd", day)
        .replace("HH", hours)
        .replace("hh", hours)
        .replace("MM", minutes)
        .replace("A", period);
    },
    formatDayAriaLabel(day) {
      const date = day.date;
      const month = this.monthNames[date.getMonth()];
      let label = `${day.day} ${month} ${date.getFullYear()}`;
      if (day.isInactive) label += " (other month)";
      if (this.isSelectedStart(date)) label += " (range start)";
      else if (this.isSelectedEnd(date)) label += " (range end)";
      else if (this.isInRange(date)) label += " (in range)";
      return label;
    },
    toggleCalendar() {
      if (this.disabled || this.inline) return;
      this.showCalendar = !this.showCalendar;
      if (this.showCalendar && !this.showTimeOnly && !this.selectedRange[0]) {
        const currentDate = new Date();
        this.selectedRange = [new Date(currentDate), null];
        this.currentMonths = Array.from(
          { length: this.numberOfMonths },
          (_, i) => {
            let month = currentDate.getMonth() + i;
            let year = currentDate.getFullYear();
            if (month > 11) {
              month = month % 12;
              year += Math.floor((currentDate.getMonth() + i) / 12);
            }
            return { month, year };
          }
        );
        this.adjustTimeForToday();
        this.$nextTick(() => this.focusSelectedDay());
      } else if (this.showCalendar && this.showTimeOnly) {
        this.$nextTick(() => this.$refs.hourInput.focus());
      }
    },
    adjustTimeForToday() {
      if (!this.isSelectedDateToday || !this.showTime) return;
      const currentDate = new Date();
      if (
        this.hours < currentDate.getHours() ||
        (this.hours === currentDate.getHours() &&
          this.minutes <= currentDate.getMinutes())
      ) {
        this.hours = currentDate.getHours();
        this.minutes = currentDate.getMinutes() + 1;
        this.period = this.hours >= 12 ? "PM" : "AM";
      }
    },
    previousMonth() {
      this.currentMonths = this.currentMonths.map(({ month, year }) => {
        month--;
        if (month < 0) {
          month = 11;
          year--;
        }
        return { month, year };
      });
      this.focusSelectedDay();
    },
    nextMonth() {
      if (this.isNextMonthDisabled(this.numberOfMonths - 1)) return;
      this.currentMonths = this.currentMonths.map(({ month, year }) => {
        month++;
        if (month > 11) {
          month = 0;
          year++;
        }
        return { month, year };
      });
      this.focusSelectedDay();
    },
    selectDate(date) {
      if (this.isBeforeMinDate(date) || this.isAfterMaxDate(date)) return;
      const newDate = new Date(date);
      if (this.showTime) {
        newDate.setHours(this.hours, this.minutes, 0);
      } else {
        newDate.setHours(0, 0, 0, 0);
      }

      if (this.selectionMode === "single") {
        this.selectedRange = [newDate, null];
        this.adjustTimeForToday();
        this.emitModelValue();
        if (!this.inline) this.showCalendar = false;
      } else {
        const [start, end] = this.selectedRange;
        if (!start || end) {
          this.selectedRange = [newDate, null];
          this.adjustTimeForToday();
        } else {
          this.selectedRange = [
            newDate < start ? newDate : start,
            newDate < start ? start : newDate,
          ];
        }
        this.emitModelValue();
      }
      this.$nextTick(() => this.$refs.input?.focus());
    },
    emitModelValue() {
      if (this.showTimeOnly) {
        let timeValue = "";
        if (this.hours !== null && this.minutes !== null) {
          if (this.hourFormat === "12") {
            timeValue = `${this.pad(this.displayHours)}:${this.pad(
              this.minutes
            )} ${this.period}`;
          } else {
            timeValue = `${this.pad(this.hours)}:${this.pad(this.minutes)}`;
          }
        }
        if (timeValue !== this.modelValue) {
          this.$emit("update:modelValue", timeValue);
          this.$emit("onChangeDate", timeValue);
        }
      } else if (this.selectionMode === "single") {
        const date = this.selectedRange[0];
        const newValue = date ? date.toISOString() : null;
        if (newValue !== this.modelValue) {
          this.$emit("update:modelValue", newValue);
          this.$emit("onChangeDate", newValue);
        }
      } else {
        const [start, end] = this.selectedRange;
        const newValue = [
          start ? start.toISOString() : null,
          end ? end.toISOString() : null,
        ];
        const currentValue = Array.isArray(this.modelValue)
          ? this.modelValue
          : [null, null];
        if (
          newValue[0] !== currentValue[0] ||
          newValue[1] !== currentValue[1]
        ) {
          this.$emit("update:modelValue", newValue);
          this.$emit("onChangeDate", newValue);
        }
      }
    },
    incrementHours() {
      if (this.isIncrementHoursDisabled) return;
      if (this.hourFormat === "12") {
        if (this.hours === 11) {
          this.hours = 0;
          this.togglePeriod();
        } else if (this.hours === 23) {
          this.hours = 12;
        } else {
          this.hours++;
        }
      } else {
        this.hours = (this.hours + 1) % 24;
      }
      this.adjustTimeForToday();
      this.updateTime();
    },
    decrementHours() {
      if (this.isDecrementHoursDisabled) return;
      if (this.hourFormat === "12") {
        if (this.hours === 0) {
          this.hours = 11;
          this.togglePeriod();
        } else if (this.hours === 12) {
          this.hours = 23;
        } else {
          this.hours--;
        }
      } else {
        this.hours = (this.hours - 1 + 24) % 24;
      }
      this.adjustTimeForToday();
      this.updateTime();
    },
    incrementMinutes() {
      if (this.isIncrementMinutesDisabled) return;
      this.minutes = (this.minutes + 1) % 60;
      this.adjustTimeForToday();
      this.updateTime();
    },
    decrementMinutes() {
      if (this.isDecrementMinutesDisabled) return;
      this.minutes = (this.minutes - 1 + 60) % 60;
      this.adjustTimeForToday();
      this.updateTime();
    },
    togglePeriod() {
      this.period = this.period === "AM" ? "PM" : "AM";
      this.hours = this.hours < 12 ? this.hours + 12 : this.hours - 12;
      this.adjustTimeForToday();
      this.updateTime();
    },
    updateTime() {
      if (this.showTimeOnly) {
        this.emitModelValue();
      } else if (this.selectionMode === "single" && this.selectedRange[0]) {
        const newDate = new Date(this.selectedRange[0]);
        newDate.setHours(this.hours, this.minutes, 0);
        this.selectedRange[0] = newDate;
        this.emitModelValue();
      } else if (this.selectionMode === "range") {
        const [start, end] = this.selectedRange;
        if (start) {
          const newStart = new Date(start);
          newStart.setHours(this.hours, this.minutes, 0);
          this.selectedRange[0] = newStart;
        }
        if (end) {
          const newEnd = new Date(end);
          newEnd.setHours(this.hours, this.minutes, 0);
          this.selectedRange[1] = newEnd;
        }
        this.emitModelValue();
      }
    },
    handleTimeKeydown(event, unit) {
      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (unit === "hours" && !this.isIncrementHoursDisabled)
          this.incrementHours();
        else if (unit === "minutes" && !this.isIncrementMinutesDisabled)
          this.incrementMinutes();
        else if (unit === "period") this.togglePeriod();
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        if (unit === "hours" && !this.isDecrementHoursDisabled)
          this.decrementHours();
        else if (unit === "minutes" && !this.isDecrementMinutesDisabled)
          this.decrementMinutes();
        else if (unit === "period") this.togglePeriod();
      } else if (event.key === "Tab" && !event.shiftKey) {
        event.preventDefault();
        if (unit === "hours") this.$refs.minuteInput.focus();
        else if (unit === "minutes" && this.hourFormat === "12")
          this.$refs.periodInput.focus();
        else if (
          (unit === "minutes" && this.hourFormat !== "12") ||
          unit === "period"
        ) {
          if (this.showButtonBar && !this.showTimeOnly) {
            this.$refs.clearButton?.focus() || this.$refs.todayButton.focus();
          }
        }
      }
    },
    isSelectedStart(date) {
      if (!this.selectedRange[0]) return false;
      return date.toDateString() === this.selectedRange[0].toDateString();
    },
    isSelectedEnd(date) {
      if (!this.selectedRange[1]) return false;
      return date.toDateString() === this.selectedRange[1].toDateString();
    },
    isInRange(date) {
      if (!this.selectedRange[0] || !this.selectedRange[1]) return false;
      const start = new Date(this.selectedRange[0]);
      const end = new Date(this.selectedRange[1]);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
      return date > start && date < end;
    },
    isToday(date) {
      return date.toDateString() === new Date().toDateString();
    },
    isBeforeMinDate(date) {
      if (!this.minDate) return false;
      const minDateCopy = new Date(this.minDate);
      minDateCopy.setHours(0, 0, 0, 0);
      return date < minDateCopy;
    },
    isAfterMaxDate(date) {
      if (!this.maxDate) return false;
      const maxDateCopy = new Date(this.maxDate);
      maxDateCopy.setHours(0, 0, 0, 0);
      return date > maxDateCopy;
    },
    clearDate() {
      this.selectedRange = [null, null];
      const currentDate = new Date();
      this.hours = currentDate.getHours();
      this.minutes = currentDate.getMinutes();
      this.period = this.hours >= 12 ? "PM" : "AM";
      this.currentMonths = Array.from(
        { length: this.numberOfMonths },
        (_, i) => {
          let month = currentDate.getMonth() + i;
          let year = currentDate.getFullYear();
          if (month > 11) {
            month = month % 12;
            year += Math.floor((currentDate.getMonth() + i) / 12);
          }
          return { month, year };
        }
      );
      this.emitModelValue();
      if (!this.inline) this.showCalendar = false;
      this.$refs.input?.focus();
    },
    selectToday() {
      const currentDate = new Date();
      if (this.showTimeOnly) {
        this.hours = currentDate.getHours();
        this.minutes = currentDate.getMinutes();
        this.period = this.hours >= 12 ? "PM" : "AM";
        this.emitModelValue();
        if (!this.inline) this.showCalendar = false;
        this.$refs.input?.focus();
      } else {
        const today = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          currentDate.getDate()
        );
        today.setHours(
          this.showTime ? this.hours : 0,
          this.showTime ? this.minutes : 0,
          0
        );
        this.selectedRange =
          this.selectionMode === "single" ? [today, null] : [today, null];
        this.currentMonths = Array.from(
          { length: this.numberOfMonths },
          (_, i) => {
            let month = currentDate.getMonth() + i;
            let year = currentDate.getFullYear();
            if (month > 11) {
              month = month % 12;
              year += Math.floor((currentDate.getMonth() + i) / 12);
            }
            return { month, year };
          }
        );
        this.period = this.hours >= 12 ? "PM" : "AM";
        this.adjustTimeForToday();
        this.emitModelValue();
        if (!this.inline) this.showCalendar = false;
        this.$refs.input?.focus();
      }
    },
    handleClickOutside(event) {
      if (
        this.inline ||
        !this.$refs.calendarContainer ||
        this.$refs.calendarContainer.contains(event.target)
      )
        return;
      this.showCalendar = false;
    },
    handleInputKeydown(event) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.toggleCalendar();
      }
    },
    handleIconKeydown(event) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.toggleCalendar();
      }
    },
    handleDayKeydown(event, index, monthIndex) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.selectDate(this.calendarDays[monthIndex][index].date);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        if (
          index % 7 === 6 &&
          monthIndex < this.numberOfMonths - 1 &&
          this.calendarDays[monthIndex + 1][index + 1 - 7]
        ) {
          this.focusDay(index + 1 - 7, monthIndex + 1);
        } else {
          this.focusDay(index + 1, monthIndex);
        }
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        if (
          index % 7 === 0 &&
          monthIndex > 0 &&
          this.calendarDays[monthIndex - 1][index - 1 + 7]
        ) {
          this.focusDay(index - 1 + 7, monthIndex - 1);
        } else {
          this.focusDay(index - 1, monthIndex);
        }
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        this.focusDay(index + 7, monthIndex);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        this.focusDay(index - 7, monthIndex);
      } else if (event.key === "Tab" && !event.shiftKey) {
        event.preventDefault();
        if (this.showTime || this.showTimeOnly) this.$refs.hourInput.focus();
        else if (this.showButtonBar)
          this.$refs.clearButton?.focus() || this.$refs.todayButton.focus();
      }
    },
    focusDay(index, monthIndex) {
      if (
        index >= 0 &&
        index < this.calendarDays[monthIndex].length &&
        monthIndex >= 0 &&
        monthIndex < this.numberOfMonths
      ) {
        this.focusedDayIndex = index;
        const dayElements = this.$refs.calendarPanel.querySelectorAll(
          `.calendar-month:nth-child(${monthIndex + 1}) .day`
        );
        const dayElement = dayElements[index];
        dayElement?.focus();
      }
    },
    focusSelectedDay() {
      let monthIndexToFocus = 0;
      let indexToFocus = -1;
      for (let m = 0; m < this.numberOfMonths; m++) {
        indexToFocus = this.calendarDays[m].findIndex((day) =>
          this.selectionMode === "single" || !this.selectedRange[1]
            ? this.isSelectedStart(day.date)
            : this.isSelectedEnd(day.date)
        );
        if (indexToFocus !== -1) {
          monthIndexToFocus = m;
          break;
        }
      }
      if (indexToFocus === -1) {
        for (let m = 0; m < this.numberOfMonths; m++) {
          indexToFocus = this.calendarDays[m].findIndex(
            (day) => !day.isInactive
          );
          if (indexToFocus !== -1) {
            monthIndexToFocus = m;
            break;
          }
        }
      }
      if (indexToFocus !== -1) this.focusDay(indexToFocus, monthIndexToFocus);
    },
  },
  watch: {
    showCalendar(newVal) {
      if (newVal && !this.showTimeOnly && !this.selectedRange[0]) {
        const currentDate = new Date();
        this.selectedRange = [new Date(currentDate), null];
        this.currentMonths = Array.from(
          { length: this.numberOfMonths },
          (_, i) => {
            let month = currentDate.getMonth() + i;
            let year = currentDate.getFullYear();
            if (month > 11) {
              month = month % 12;
              year += Math.floor((currentDate.getMonth() + i) / 12);
            }
            return { month, year };
          }
        );
        this.adjustTimeForToday();
        this.$nextTick(() => this.focusSelectedDay());
      } else if (newVal && this.showTimeOnly) {
        this.$nextTick(() => this.$refs.hourInput.focus());
      }
    },
    modelValue(newVal) {
      if (this.showTimeOnly && typeof newVal === "string" && newVal) {
        const [time, period] = newVal.includes(" ")
          ? newVal.split(" ")
          : [newVal, this.hourFormat === "12" ? "AM" : null];
        const [hoursStr, minutesStr] = time.split(":");
        const hours = parseInt(hoursStr, 10);
        const minutes = parseInt(minutesStr, 10);

        this.hours = isNaN(hours) ? 0 : hours;
        this.minutes = isNaN(minutes) ? 0 : minutes;

        if (this.hourFormat === "12") {
          this.period = period && ["AM", "PM"].includes(period) ? period : "AM";
          this.hours =
            this.period === "PM" && hours < 12
              ? hours + 12
              : this.period === "AM" && hours === 12
              ? 0
              : hours;
        } else if (this.hourFormat === "24" && !isNaN(hours)) {
          this.hours = hours % 24;
        }
      } else if (
        this.selectionMode === "single" &&
        newVal &&
        !this.showTimeOnly
      ) {
        const newDate = new Date(newVal);
        this.selectedRange = [newDate, null];
        this.currentMonths = Array.from(
          { length: this.numberOfMonths },
          (_, i) => {
            let month = newDate.getMonth() + i;
            let year = newDate.getFullYear();
            if (month > 11) {
              month = month % 12;
              year += Math.floor((newDate.getMonth() + i) / 12);
            }
            return { month, year };
          }
        );
        this.hours = newDate.getHours();
        this.minutes = newDate.getMinutes();
        this.period = this.hours >= 12 ? "PM" : "AM";
        this.adjustTimeForToday();
      } else if (Array.isArray(newVal) && !this.showTimeOnly) {
        this.selectedRange = [
          newVal[0] ? new Date(newVal[0]) : null,
          newVal[1] ? new Date(newVal[1]) : null,
        ];
        const date = this.selectedRange[0] || this.selectedRange[1];
        if (date) {
          this.currentMonths = Array.from(
            { length: this.numberOfMonths },
            (_, i) => {
              let month = date.getMonth() + i;
              let year = date.getFullYear();
              if (month > 11) {
                month = month % 12;
                year += Math.floor((date.getMonth() + i) / 12);
              }
              return { month, year };
            }
          );
          this.hours = date.getHours();
          this.minutes = date.getMinutes();
          this.period = this.hours >= 12 ? "PM" : "AM";
          this.adjustTimeForToday();
        } else {
          const currentDate = new Date();
          this.hours = currentDate.getHours();
          this.minutes = currentDate.getMinutes();
          this.period = this.hours >= 12 ? "PM" : "AM";
        }
      }
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    if (this.inline && !this.showTimeOnly && !this.selectedRange[0]) {
      const currentDate = new Date();
      this.selectedRange = [new Date(currentDate), null];
      this.currentMonths = Array.from(
        { length: this.numberOfMonths },
        (_, i) => {
          let month = currentDate.getMonth() + i;
          let year = currentDate.getFullYear();
          if (month > 11) {
            month = month % 12;
            year += Math.floor((currentDate.getMonth() + i) / 12);
          }
          return { month, year };
        }
      );
      this.period = this.hours >= 12 ? "PM" : "AM";
      this.adjustTimeForToday();
      this.$nextTick(() => this.focusSelectedDay());
    }
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
.calendar-input-container {
  position: relative;
  width: 300px;
  font-family: "voyado Text", sans-serif;
}

.calendar-input-container.inline {
  width: auto;
}

.input-wrapper {
  position: relative;
}

.calendar-input {
  width: 100%;
  height: 40px;
  padding: 10px;
  padding-right: 40px;
  font-size: 16px;
  color: #21211f;
  border: 1px solid #c0c0bc;
  border-radius: 0.25rem;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  font-family: "Voyado Text";
}

.calendar-input:focus {
  outline: 2px solid #222222;
  border-style: double;
  box-shadow: none;
}

.calendar-input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.p-invalid {
  border-color: #dc3545;
}

.p-error {
  color: #dc3545;
  font-size: 14px;
}

.calendar-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 16px;
}

.calendar-icon:focus {
  border-radius: 4px;
}

.calendar-container {
  position: absolute;
  bottom: 100%;
  top: auto;
  left: 0;
  width: 100%;
  border: 1px solid #c0c0bc;
  border-radius: 0.25rem;
  padding: 10px;
  background: white;
  z-index: 1006;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.calendar-container.calendar-downside {
  top: 100%;
  bottom: auto;
}

.calendar-container-width1 {
  width: 300px;
}

.calendar-container-width2 {
  width: 600px;
}

.calendar-input-container.inline .calendar-container {
  position: static;
  box-shadow: none;
}

.calendar-month-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.calendar-month {
  flex: 1;
  min-width: 250px;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  font-weight: 500;
}

.nav-button {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 5px 10px;
}

.nav-button:disabled {
  color: #c0c0bc;
  cursor: not-allowed;
}

.nav-button:focus {
  border-radius: 4px;
}

.month-year {
  font-size: 16px;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.day-header {
  text-align: center;
  padding: 5px;
  font-weight: 500;
  font-size: 14px;
  color: #21211f;
}

.day {
  text-align: center;
  padding: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #21211f;
  border-radius: 4px;
}

.day:hover:not(.disabled):not(.selected-start):not(.selected-end) {
  background: #f0f0f0;
}

.day:focus {
  outline: 2px solid #222222;
}

.inactive {
  color: #666;
}

.selected-start,
.selected-end {
  background: #222222;
  color: white;
}

.range {
  background: #e0e0e0;
  color: #21211f;
}

.today {
  border: 1px solid #222222;
}

.disabled {
  color: #c0c0bc;
  cursor: not-allowed;
  background: #f5f5f5;
}

.time-picker {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
}

.time-unit {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
}

.time-arrow {
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  line-height: 1;
}

.time-arrow i {
  font-size: 12px;
  display: inline-block;
}

.time-value {
  font-size: 16px;
  font-weight: 500;
  color: #21211f;
  padding: 4px 8px;
  text-align: center;
  min-width: 32px;
  display: inline-block;
  border-radius: 4px;
  margin: -11px;
  margin-top: -22px;
}

.time-value:focus {
  outline: 2px solid #222222;
  background: #f0f0f0;
}

.time-separator {
  font-size: 16px;
  font-weight: 500;
  color: #21211f;
  padding: 0 4px;
}

.button-bar {
  display: flex;
  justify-content: space-between;
  padding: 10px;
}

.clear-button,
.today-button {
  background: #f0f0f0;
  border: none;
  padding: 5px 10px;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 14px;
}

.clear-button:hover,
.today-button:hover {
  background: #e0e0e0;
}

.clear-button:focus,
.today-button:focus {
  outline: 2px solid #222222;
}
</style>